import {
  FC,
  MutableRefObject,
  RefCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import AnchorDataOverviewFieldEditor from "@/components/anchorDataOverview/anchorDataOverviewFieldEditor";

type AnchorDataOverviewCellInlineEditorProps = {
  row: any;
  columnId: string;
  template: any;
  templateType: string;
  editedInlineEditorSx: Record<string, any>;
  overviewBaseRows: any[];
  pendingInlineEdits: Record<string, Record<string, any>>;
  pendingResetToInherited: Record<string, Record<string, boolean>>;
  isSavingInline: boolean;
  setPendingInlineEdits: any;
  setPendingResetToInherited: any;
  handleSaveCell: (
    subjectId: string,
    columnId: string,
    override?: { value: any; resetToInherited: boolean },
  ) => void;
  handleDiscardCell: (subjectId: string, columnId: string) => void;
  getNextCell: (rowId: string, columnId: string) => [string, string] | null;
  getPrevCell: (rowId: string, columnId: string) => [string, string] | null;
  inlineInputRefs: MutableRefObject<Record<string, HTMLInputElement | null>>;
  formatInheritedForDisplay: (value: any, type: string) => string;
  isSameAnchorDataValue: (a: any, b: any, type: string) => boolean;
  onLocalDirtyChange: (
    subjectId: string,
    columnId: string,
    isDirty: boolean,
  ) => void;
  onEmailValidationChange: (
    subjectId: string,
    columnId: string,
    isInvalid: boolean,
  ) => void;
  onPhoneValidationChange: (
    subjectId: string,
    columnId: string,
    isInvalid: boolean,
  ) => void;
  enumOptions?: { value: string; label: string }[];
  enumResetSignal?: number;
  incrementEnumResetSignal?: (subjectId: string, columnId: string) => void;
  loadOptionsForSubjectIdList?: (inputValue: string) => Promise<any[]>;
  loadOptionsForWzCodeIdList?: (inputValue: string) => Promise<any[]>;
  validationTargetRef?: RefCallback<HTMLElement>;
};

const AnchorDataOverviewCellInlineEditor: FC<
  AnchorDataOverviewCellInlineEditorProps
> = ({
  row,
  columnId,
  template,
  templateType,
  editedInlineEditorSx,
  overviewBaseRows,
  pendingInlineEdits,
  pendingResetToInherited,
  isSavingInline,
  setPendingInlineEdits,
  setPendingResetToInherited,
  handleSaveCell,
  handleDiscardCell,
  getNextCell,
  getPrevCell,
  inlineInputRefs,
  formatInheritedForDisplay,
  isSameAnchorDataValue,
  onLocalDirtyChange,
  onEmailValidationChange,
  onPhoneValidationChange,
  enumOptions,
  enumResetSignal,
  incrementEnumResetSignal,
  loadOptionsForSubjectIdList,
  loadOptionsForWzCodeIdList,
  validationTargetRef,
}) => {
  const subjectId = row.original.id;
  const originalRow =
    overviewBaseRows.find((r: any) => r.id === subjectId) ?? row.original;
  const currentValue =
    pendingInlineEdits[subjectId]?.[columnId] !== undefined
      ? pendingInlineEdits[subjectId][columnId]
      : originalRow[columnId] ?? "";
  const resetToInherited =
    pendingResetToInherited[subjectId]?.[columnId] ?? false;
  const [localValue, setLocalValue] = useState(currentValue);
  /** Ref to avoid stale localValue in onBlur when dropdown onChange and onBlur fire in same tick */
  const latestOnChangeValueRef = useRef<any>(undefined);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  const cellKey = `${subjectId}-${columnId}`;
  const nextCell = getNextCell(subjectId, columnId);
  const prevCell = getPrevCell(subjectId, columnId);
  const originalValue = originalRow[columnId];
  const hasLocalChange = !isSameAnchorDataValue(
    localValue,
    originalValue,
    templateType,
  );
  const hasCellChange =
    hasLocalChange ||
    pendingInlineEdits[subjectId]?.[columnId] !== undefined ||
    !!pendingResetToInherited[subjectId]?.[columnId];
  const hasUnflushedLocalChange = !isSameAnchorDataValue(
    localValue,
    currentValue,
    templateType,
  );
  const inheritedValue = originalRow[`inherited_value_${template.value}`];
  const prevUnflushedRef = useRef(hasUnflushedLocalChange);

  useEffect(() => {
    if (prevUnflushedRef.current === hasUnflushedLocalChange) return;
    prevUnflushedRef.current = hasUnflushedLocalChange;
    onLocalDirtyChange(subjectId, columnId, hasUnflushedLocalChange);
  }, [hasUnflushedLocalChange, onLocalDirtyChange, subjectId, columnId]);

  useEffect(
    () => () => {
      onLocalDirtyChange(subjectId, columnId, false);
    },
    [onLocalDirtyChange, subjectId, columnId],
  );

  return (
    <Box
      ref={validationTargetRef}
      data-anchor-data-cell-key={`${subjectId}::${columnId}`}
      className={hasCellChange ? "anchordata-inline-edited-cell" : undefined}
      sx={{
        width: "100%",
        ...(hasCellChange ? editedInlineEditorSx : {}),
      }}
    >
      <AnchorDataOverviewFieldEditor
        template={template}
        templateType={templateType}
        value={localValue}
        enumOptions={enumOptions}
        enumResetSignal={enumResetSignal}
        loadOptionsForSubjectIdList={loadOptionsForSubjectIdList}
        loadOptionsForWzCodeIdList={loadOptionsForWzCodeIdList}
        hasCellChange={hasCellChange}
        onSaveCell={() =>
          handleSaveCell(subjectId, columnId, {
            value: localValue,
            resetToInherited,
          })
        }
        onDiscardCell={() => {
          incrementEnumResetSignal?.(subjectId, columnId);
          setLocalValue(originalRow[columnId] ?? "");
          handleDiscardCell(subjectId, columnId);
        }}
        isSavingCell={isSavingInline}
        onChange={(v) => {
          latestOnChangeValueRef.current = v;
          setLocalValue(v);
          // Enum: only flush to pendingInlineEdits when user selected a non-empty value.
          // DropDown fires onChange(undefined) on mount (values=[]) before defaultValue syncs;
          // flushing that would mark cells that already have a value as changed (green). Only
          // flush when the new value is non-empty (real selection). Clearing is persisted on blur.
          if (
            templateType === "enum" &&
            v != null &&
            v !== "" &&
            !isSameAnchorDataValue(v, originalValue, templateType)
          ) {
            setPendingInlineEdits(
              (prev: Record<string, Record<string, any>>) => ({
                ...prev,
                [subjectId]: {
                  ...(prev[subjectId] ?? {}),
                  [columnId]: v,
                },
              }),
            );
          }
          // subject_id_list, wz_code_id_list, date:
          // flush when value changed; clear pending edit when reverted to original.
          if (
            templateType === "subject_id_list" ||
            templateType === "wz_code_id_list" ||
            templateType === "date"
          ) {
            if (isSameAnchorDataValue(v, originalValue, templateType)) {
              setPendingInlineEdits(
                (prev: Record<string, Record<string, any>>) => {
                  const rowEdits = { ...(prev[subjectId] ?? {}) };
                  delete rowEdits[columnId];
                  const next = { ...prev };
                  if (Object.keys(rowEdits).length === 0)
                    delete next[subjectId];
                  else next[subjectId] = rowEdits;
                  return next;
                },
              );
            } else {
              setPendingInlineEdits(
                (prev: Record<string, Record<string, any>>) => ({
                  ...prev,
                  [subjectId]: {
                    ...(prev[subjectId] ?? {}),
                    [columnId]: v,
                  },
                }),
              );
            }
          }
        }}
        onBlur={() => {
          const valueForBlur =
            latestOnChangeValueRef.current !== undefined
              ? latestOnChangeValueRef.current
              : localValue;
          latestOnChangeValueRef.current = undefined;
          if (
            isSameAnchorDataValue(valueForBlur, originalValue, templateType)
          ) {
            setPendingInlineEdits(
              (prev: Record<string, Record<string, any>>) => {
                const rowEdits = { ...(prev[subjectId] ?? {}) };
                delete rowEdits[columnId];
                const next = { ...prev };
                if (Object.keys(rowEdits).length === 0) delete next[subjectId];
                else next[subjectId] = rowEdits;
                return next;
              },
            );
            setPendingResetToInherited(
              (prev: Record<string, Record<string, boolean>>) => {
                const rowResets = { ...(prev[subjectId] ?? {}) };
                delete rowResets[columnId];
                const next = { ...prev };
                if (Object.keys(rowResets).length === 0) delete next[subjectId];
                else next[subjectId] = rowResets;
                return next;
              },
            );
          } else {
            setPendingInlineEdits(
              (prev: Record<string, Record<string, any>>) => ({
                ...prev,
                [subjectId]: {
                  ...(prev[subjectId] ?? {}),
                  [columnId]: valueForBlur,
                },
              }),
            );
          }
        }}
        row={originalRow}
        resetToInherited={resetToInherited}
        onResetToInheritedChange={(v) => {
          setPendingResetToInherited(
            (prev: Record<string, Record<string, boolean>>) => ({
              ...prev,
              [subjectId]: {
                ...(prev[subjectId] ?? {}),
                [columnId]: v,
              },
            }),
          );
          if (v) {
            const val =
              inheritedValue != null &&
              typeof inheritedValue === "object" &&
              "value" in inheritedValue
                ? (inheritedValue as { value: any }).value
                : inheritedValue;
            setPendingInlineEdits(
              (prev: Record<string, Record<string, any>>) => ({
                ...prev,
                [subjectId]: {
                  ...(prev[subjectId] ?? {}),
                  [columnId]: val,
                },
              }),
            );
          }
        }}
        formatInheritedForDisplay={formatInheritedForDisplay}
        isSameAnchorDataValue={isSameAnchorDataValue}
        onEmailValidationChange={
          templateType === "email"
            ? (isInvalid: boolean) =>
                onEmailValidationChange(subjectId, columnId, isInvalid)
            : undefined
        }
        onPhoneValidationChange={
          templateType === "phone_number"
            ? (isInvalid: boolean) =>
                onPhoneValidationChange(subjectId, columnId, isInvalid)
            : undefined
        }
        variant="inline"
        autoFocus={false}
        inputRef={(el) => {
          inlineInputRefs.current[cellKey] = el;
        }}
        onTabNext={
          nextCell
            ? () =>
                inlineInputRefs.current[
                  `${nextCell[0]}-${nextCell[1]}`
                ]?.focus()
            : undefined
        }
        onTabPrev={
          prevCell
            ? () =>
                inlineInputRefs.current[
                  `${prevCell[0]}-${prevCell[1]}`
                ]?.focus()
            : undefined
        }
      />
    </Box>
  );
};

export default AnchorDataOverviewCellInlineEditor;
